import {
  DashboardProvider,
  useDashboard,
} from "../components/dashboard/DashboardProvider";
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
} from "@mui/material";
import { downloadExcelFile } from "../utils/exportHelper";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  Download as DownloadIcon,
  ChevronDown as ChevronDownIcon,
} from "lucide-react";
import dayjs from "dayjs";

// Layout & Constants
import {
  white,
  darkBackground,
  borderGray,
  borderGrayLight,
  darkDenium,
  textGray,
} from "../constants/colors";
import {
  MainContent,
  ScrollableContent,
  OverviewRow,
} from "../components/dashboard/DashboardStyles";
import { extractId } from "../components/dashboard/DashboardUtils";

// API & Global Shared
import CommonFilters from "../components/common/commonFilters/CommonFilters";
import MobileCommonFilters from "../components/common/mobileCommonFilters/CommonFilters";

// Refactored Dashboard Components
import DateRangePicker from "../components/dashboard/DateRangePicker";
import MemberOverview from "../components/dashboard/MemberOverview";
import OutreachOutcomeSummary from "../components/dashboard/OutreachOutcomeSummary";
import ProgramPerformanceOverview from "../components/dashboard/ProgramPerformanceOverview";
import CareGuidePerformanceReport from "../components/dashboard/CareGuidePerformanceReport";
import ReasonModal from "../components/dashboard/ReasonModal";

// Local Context
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DashboardPDFExport from "../components/dashboard/DashboardPDFExport";

const DashboardPageView = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    userRole,
    isAuthorized,
    fetchData,
    fetchOptions,
    loading,
    outcomes,
    caregivers,
    programPerf,
    carrierFilter,
    setCarrierFilter,
    groupFilter,
    setGroupFilter,
    carriers,
    groups,
    selectedCarrierIds,
    setSelectedCarrierIds,
    selectedGroupIds,
    setSelectedGroupIds,
    dateRange,
    setDateRange,
    dropdownLoading,
    dropdownHasMore,
    pagination,
    setPagination,
    carrierToGroupsMap,
    groupToCarrierMap,
    memberOverviewCalculated,
    apiDateRange,
  } = useDashboard();
  const pdfExportRef = useRef(null);

  // Local UI-only State
  // const [navModal, setNavModal] = useState({ open: false, name: "", id: "" });
  // Kept if needed elsewhere, but mostly for removal later if cleanup is desired.
  const [reasonModal, setReasonModal] = useState({
    open: false,
    label: "",
    subReasons: [],
  });
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const isExportMenuOpen = Boolean(exportAnchorEl);
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };
  const handleExportClose = () => {
    setExportAnchorEl(null);
  };
  const [carrierSearch, setCarrierSearch] = useState("");
  const [groupSearch, setGroupSearch] = useState("");

  const [exportExcelLoading, setExportExcelLoading] = useState(false);

  const handleExportExcel = async () => {
    setExportExcelLoading(true);
    try {
      const payload = {
        startDate: dateRange?.startDate
          ? dayjs(dateRange.startDate).format("YYYY-MM-DD")
          : "",
        endDate: dateRange?.endDate
          ? dayjs(dateRange.endDate).format("YYYY-MM-DD")
          : "",
        carrierCodes: selectedCarrierIds || [],
        groupNumbers: selectedGroupIds || [],
      };
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
      await downloadExcelFile({
        url: "/member-dashboard/performances/export",
        method: "POST",
        payload,
        filename: `Dashboard_${timestamp}.xlsx`,
      });
    } catch (error) {
      console.error("Dashboard export failed:", error);
    } finally {
      setExportExcelLoading(false);
    }
  };

  // Initial Data Fetch and Options
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchOptions("CARRIER", "", true);
    fetchOptions("GROUP", "", true);
  }, [fetchOptions]);

  // Handlers
  const handleCarrierChange = useCallback(
    (val) => {
      const names = Array.isArray(val) ? val : [val].filter(Boolean);
      const codes = names.map(extractId).filter(Boolean);
      setCarrierFilter(names);
      setSelectedCarrierIds(codes);
      if (names.length === 0) {
        setGroupFilter([]);
        setSelectedGroupIds([]);
      } else {
        const gs = new Set();
        const gids = new Set();
        names.forEach((n) =>
          (carrierToGroupsMap[n] || []).forEach((g) => {
            gs.add(`${g.groupName} (${g.groupNumber})`);
            gids.add(String(g.groupNumber));
          }),
        );
        setGroupFilter(Array.from(gs));
        setSelectedGroupIds(Array.from(gids));
      }
    },
    [
      carrierToGroupsMap,
      setCarrierFilter,
      setSelectedCarrierIds,
      setGroupFilter,
      setSelectedGroupIds,
    ],
  );

  const handleGroupChange = useCallback(
    (val) => {
      const names = Array.isArray(val) ? val : [val].filter(Boolean);
      const codes = names.map(extractId).filter(Boolean);
      setGroupFilter(names);
      setSelectedGroupIds(codes);
      const cs = new Set();
      const cids = new Set();
      names.forEach((n) => {
        const info = groupToCarrierMap[n];
        if (info) {
          cs.add(info.display);
          cids.add(String(info.code));
        }
      });
      setCarrierFilter(Array.from(cs));
      setSelectedCarrierIds(Array.from(cids));
    },
    [
      groupToCarrierMap,
      setGroupFilter,
      setSelectedGroupIds,
      setCarrierFilter,
      setSelectedCarrierIds,
    ],
  );

  const handleDropdownOpen = useCallback(
    (type) => {
      const hasData =
        type === "CARRIER" ? carriers.length > 0 : groups.length > 0;
      if (hasData) return;
      fetchOptions(
        type,
        type === "CARRIER" ? carrierSearch : groupSearch,
        0,
        true,
      );
      setPagination((p) => ({ ...p, [type]: 0 }));
    },
    [
      fetchOptions,
      carriers.length,
      groups.length,
      carrierSearch,
      groupSearch,
      setPagination,
    ],
  );

  const handleLoadMore = useCallback(
    (type) => {
      const next = pagination[type] + 1;
      fetchOptions(
        type,
        type === "CARRIER" ? carrierSearch : groupSearch,
        next,
        false,
      );
      setPagination((p) => ({ ...p, [type]: next }));
    },
    [fetchOptions, pagination, carrierSearch, groupSearch, setPagination],
  );

  const handleSearch = useCallback(
    (t, ty) => {
      if (ty === "CARRIER") setCarrierSearch(t);
      else setGroupSearch(t);
      fetchOptions(ty, t, true);
      setPagination((p) => ({ ...p, [ty]: 0 }));
    },
    [fetchOptions, setPagination],
  );

  // Derived Memos
  const sortedCarriers = useMemo(() => {
    const selected = carriers.filter((c) => carrierFilter.includes(c));
    const unselected = carriers.filter((c) => !carrierFilter.includes(c));
    return [...selected, ...unselected];
  }, [carriers, carrierFilter]);

  const filteredGroups = useMemo(() => {
    const selected = groups.filter((g) => groupFilter.includes(g));
    const unselected = groups.filter((g) => !groupFilter.includes(g));
    return [...selected, ...unselected];
  }, [groups, groupFilter]);

  const mergedProgramData = useMemo(() => {
    return programPerf
      ?.map((p) => ({
        name: p.programName || p.name,
        eligible: p.eligibleTotal ?? p.eligibleCount ?? "-",
        enrolled: p.enrolledTotal ?? p.enrollmentCount ?? "-",
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [programPerf]);

  const careGuideData = useMemo(() => {
    return caregivers
      .map((cg) => ({
        name: cg.assignedCareGuide || "Unassigned",
        id: cg.assignedCareGuideId,
        total: cg.total ?? "-",
        completed: cg.completed ?? "-",
        notCompleted: cg.notCompleted ?? "-",
      }))
      .sort((a, b) => {
        if (a.name === "Unassigned") return -1;
        if (b.name === "Unassigned") return 1;
        return a.name.localeCompare(b.name);
      });
  }, [caregivers]);

  const FilterComp = isMobile ? MobileCommonFilters : CommonFilters;

  return (
    <MainContent>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateRangePicker
          open={Boolean(dateAnchorEl)}
          anchorEl={dateAnchorEl}
          onClose={() => setDateAnchorEl(null)}
          currentRange={dateRange}
          apiDateRange={apiDateRange}
          userRole={userRole}
          onApply={(v) => {
            setDateRange(v);
          }}
        />
      </LocalizationProvider>

      <ScrollableContent>
        <Box sx={{ p: 2, mt: 0 }}>
          <OverviewRow>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                color: darkBackground,
                width: "91px",
              }}
            >
              Dashboard
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 1 },
                alignItems: "center",
                flexGrow: 1,
                justifyContent: { xs: "center", lg: "flex-end" },
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: { xs: "100%", lg: "auto" },
                  "& .common-filters": {
                    gap: "8px !important",
                    display: "flex !important",
                    flexDirection: { xs: "column", sm: "row" },
                    width: "100%",
                    justifyContent: { xs: "center", lg: "flex-end" },
                  },
                  "& .dropdown-icon": { right: "5px !important" },
                  "& .dropdown-menu": {
                    minWidth: "200px !important",
                    left: { xs: "0 !important", md: "-90px !important" },
                  },
                  "& .filter-input-containers": {
                    width: { xs: "100% !important", sm: "180px !important" },
                    fontWeight: 600,
                    height: "32px !important",
                  },
                  "& .dropdown-menu.multiple": {
                    width: "100% !important",
                    minWidth: "200px !important",
                    maxWidth: "400px !important",
                  },
                  "& .filter-input": {
                    borderRadius: "20px !important",
                    height: "32px !important",
                    fontSize: "13px !important",
                    bgcolor: `${white} !important`,
                    paddingRight: "36px !important",
                  },
                }}
              >
                <FilterComp
                  carriers={sortedCarriers}
                  groups={filteredGroups}
                  carrierFilter={carrierFilter}
                  groupFilter={groupFilter}
                  onCarrierChange={handleCarrierChange}
                  onGroupChange={handleGroupChange}
                  onSearch={handleSearch}
                  onOpenDropdown={handleDropdownOpen}
                  onLoadMoreOptions={handleLoadMore}
                  loadingMap={dropdownLoading}
                  hasMoreMap={dropdownHasMore}
                  visibleFilters={["CARRIER", "GROUP"]}
                  compact
                  showLabel={false}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "center", sm: "flex-end" },
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => setDateAnchorEl(e.currentTarget)}
                  endIcon={
                    dateRange.startDate && dateRange.endDate ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CloseIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            setDateRange({ startDate: "", endDate: "" });
                          }}
                          style={{
                            fontSize: 16,
                            color: darkDenium,
                            cursor: "pointer",
                          }}
                        />
                        <CalendarTodayOutlinedIcon
                          style={{ fontSize: 16, color: darkDenium }}
                        />
                      </Box>
                    ) : (
                      <CalendarTodayOutlinedIcon
                        style={{ fontSize: 16, color: darkDenium }}
                      />
                    )
                  }
                  sx={{
                    borderRadius: "20px",
                    color: textGray,
                    borderColor: borderGray,
                    height: "32px",
                    px: 2,
                    textTransform: "none",
                    bgcolor: white,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {dateRange.startDate && dateRange.endDate
                    ? `${dayjs(dateRange.startDate).format("MM/DD/YYYY")} - ${dayjs(dateRange.endDate).format("MM/DD/YYYY")}`
                    : "Select Date Range"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon size={16} />}
                  endIcon={<ChevronDownIcon size={16} />}
                  onClick={handleExportClick}
                  disabled={exportExcelLoading}
                  sx={{
                    borderRadius: "24px",
                    color: darkDenium,
                    fontWeight: 500,
                    fontSize: "12px",
                    borderColor: borderGrayLight,
                    bgcolor: white,
                    px: 2,
                    height: "32px",
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {exportExcelLoading ? "Exporting..." : "Export"}
                </Button>
                <Menu
                  anchorEl={exportAnchorEl}
                  open={isExportMenuOpen}
                  onClose={handleExportClose}
                  slotProps={{
                    paper: {
                      sx: {
                        borderRadius: "8px",
                        mt: 0.5,
                        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                        border: `1px solid ${borderGrayLight}`,
                        minWidth: "100px",
                      },
                    },
                  }}
                >
                  <MenuItem
                    onClick={async () => {
                      handleExportClose();
                      if (pdfExportRef.current) {
                        const canvas = await html2canvas(pdfExportRef.current, {
                          scale: 2,
                          useCORS: true,
                        });
                        const imgData = canvas.toDataURL("image/png");
                        const pdf = new jsPDF("p", "pt", "a4");
                        const pdfWidth = pdf.internal.pageSize.getWidth();
                        const pdfHeight =
                          (canvas.height * pdfWidth) / canvas.width;
                        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
                        const now = new Date();
                        const pad = (n) => String(n).padStart(2, "0");
                        const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
                        pdf.save(`Dashboard_${timestamp}.pdf`);
                      }
                    }}
                    sx={{
                      fontSize: "12px",
                      color: darkDenium,
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                    }}
                  >
                    PDF
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleExportClose();
                      void handleExportExcel();
                    }}
                    disabled={exportExcelLoading}
                    sx={{
                      fontSize: "12px",
                      color: darkDenium,
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                    }}
                  >
                    Excel
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </OverviewRow>

          <Box
            display="flex"
            gap={3}
            mb={4}
            sx={{ flexDirection: { xs: "column", lg: "row" } }}
          >
            <Box flex={1}>
              <MemberOverview
                data={memberOverviewCalculated}
                loading={loading}
              />
            </Box>
            <Box flex={1}>
              <OutreachOutcomeSummary
                outcomes={outcomes}
                loading={loading}
                onOutcomeClick={(item) =>
                  setReasonModal({
                    open: true,
                    label: item.label,
                    subReasons: item.subReasons,
                  })
                }
              />
            </Box>
          </Box>

          <ProgramPerformanceOverview
            data={mergedProgramData}
            loading={loading}
          />

          {isAuthorized && (
            <CareGuidePerformanceReport
              data={careGuideData}
              loading={loading}
              onNameClick={(n, id) => {
                navigate("/members/member-outreach", {
                  state: { assignee: n, assigneeId: id },
                });
              }}
            />
          )}

          <ReasonModal
            open={reasonModal.open}
            label={reasonModal.label}
            subReasons={reasonModal.subReasons}
            onClose={() =>
              setReasonModal({ open: false, label: "", subReasons: [] })
            }
          />
        </Box>
      </ScrollableContent>
      {/* Hidden container for PDF export content */}
      <Box
        sx={{
          position: "absolute",
          top: -9000,
          left: -9990,
        }}
      >
        <div ref={pdfExportRef}>
          <DashboardPDFExport
            memberOverviewData={memberOverviewCalculated}
            outcomes={outcomes}
            mergedProgramData={mergedProgramData}
            careGuideReportData={careGuideData}
            isAuthorizedForCareGuide={isAuthorized}
            dateRange={
              dateRange?.startDate && dateRange?.endDate
                ? dateRange
                : (() => {
                    const stored = localStorage.getItem(
                      "dashboard_memberOppDateRange",
                    );
                    if (stored) {
                      try {
                        return JSON.parse(stored);
                      } catch {
                        return {};
                      }
                    }
                    return {};
                  })()
            }
            appliedFilters={{
              carriers: carrierFilter,
              groups: groupFilter,
            }}
            userRole={userRole}
            allAvailableCarriers={carriers || []}
          />
        </div>
      </Box>
    </MainContent>
  );
};

const DashboardPage = () => {
  return (
    <DashboardProvider>
      <DashboardPageView />
    </DashboardProvider>
  );
};

export default DashboardPage;
